import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import "../../assets/styles/lab-questions.css";

const HINT_THRESHOLD = 2;
const MAX_ATTEMPTS = 3;
const SHAKE_DURATION_MS = 600;
const IMAGE_LOAD_TIMEOUT_MS = 15000;

// ─── Difficulty config ───
const DIFFICULTY_LEVELS = [
  {
    key: "easy",
    label: "Easy",
    // active (matching difficulty) colors — readable on light bg
    activeBg: "rgba(46, 125, 94, 0.12)",
    activeBorder: "rgba(46, 125, 94, 0.5)",
    activeText: "#1a5a3e",
    activeDot: "#2e7d5e",
  },
  {
    key: "medium",
    label: "Medium",
    activeBg: "rgba(186, 117, 23, 0.12)",
    activeBorder: "rgba(186, 117, 23, 0.5)",
    activeText: "#8a5410",
    activeDot: "#ba7517",
  },
  {
    key: "hard",
    label: "Hard",
    activeBg: "rgba(217, 37, 29, 0.12)",
    activeBorder: "rgba(217, 37, 29, 0.5)",
    activeText: "#a31d16",
    activeDot: "#d9251d",
  },
];

// Normalize whatever the backend gives us ("Easy", "MEDIUM", "hard", "Insane"…)
const normalizeDifficulty = (value) => {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase();
  if (!v) return null;
  if (v === "easy") return "easy";
  if (v === "medium") return "medium";
  // Treat "hard", "difficult", "insane" all as the highest tier
  if (v === "hard" || v === "difficult" || v === "insane") return "hard";
  return null;
};

// ─── Utilities ───

const replaceScenarioIpPlaceholders = (value) => {
  if (typeof value !== "string") return value;
  return value.replace(/\b(?:localhost|127\.0\.0\.1)\b/g, "SCENARIO_IP");
};

// ─── TKSA Tag color map ───

// Matches a TKSA ID at the start of a string: one letter (T/K/S/A) followed
// by digits. Anything after the digits (a colon, a description, whitespace)
// is ignored. Examples:
//   "T4507"                                    → "T4507"
//   "T4507: Carry out vulnerability scanning"  → "T4507"
//   "K0100 - Knowledge of network security"    → "K0100"
const TKSA_ID_REGEX = /^([TKSA])(\d+)/i;

// Extract the bare TKSA ID from a raw value. If the input has any extra text
// (description, separator, whitespace), strip it. Returns the cleaned ID,
// or the original string if no match.
const extractTksaId = (raw) => {
  if (typeof raw !== "string") return raw;
  const match = raw.trim().match(TKSA_ID_REGEX);
  if (!match) return raw.trim();
  return `${match[1].toUpperCase()}${match[2]}`;
};

const TKSA_COLOR_MAP = {
  T: {
    bg: "rgba(55,138,221,0.12)",
    border: "rgba(55,138,221,0.35)",
    text: "#5BA3E8",
    dot: "#378ADD",
    label: "Tactic",
  },
  S: {
    bg: "rgba(99,153,34,0.12)",
    border: "rgba(99,153,34,0.35)",
    text: "#7DBF3A",
    dot: "#639922",
    label: "Skill",
  },
  A: {
    bg: "rgba(127,119,221,0.12)",
    border: "rgba(127,119,221,0.35)",
    text: "#9D96E8",
    dot: "#7F77DD",
    label: "Ability",
  },
  K: {
    bg: "rgba(186,117,23,0.12)",
    border: "rgba(186,117,23,0.35)",
    text: "#EF9F27",
    dot: "#BA7517",
    label: "Knowledge",
  },
};

const getTksaColor = (id) => {
  const prefix = id?.[0]?.toUpperCase();
  return (
    TKSA_COLOR_MAP[prefix] ?? {
      bg: "rgba(128,128,128,0.12)",
      border: "rgba(128,128,128,0.35)",
      text: "#aaa",
      dot: "#888",
      label: " ",
    }
  );
};

// ─── TksaTagRow ───

const TksaTagRow = ({ tksaIds }) => {
  if (!tksaIds || tksaIds.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "6px",
      }}
    >
      <span
        style={{
          fontSize: "10px",
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--color-text-muted, #888)",
          marginRight: "2px",
          whiteSpace: "nowrap",
        }}
      ></span>
      {Array.from(new Set(tksaIds.map(extractTksaId))).map((id) => {
        const c = getTksaColor(id);
        return (
          <span
            key={id}
            title={`${c.label}: ${id}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "11px",
              fontWeight: 600,
              fontFamily: "'Courier New', monospace",
              padding: "2px 8px",
              borderRadius: "6px",
              border: `1px solid ${c.border}`,
              background: c.bg,
              color: c.text,
              lineHeight: 1.4,
              cursor: "default",
              userSelect: "none",
            }}
          >
            <span
              style={{
                width: "5px",
                height: "5px",
                borderRadius: "50%",
                background: c.dot,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            {id}
          </span>
        );
      })}
    </div>
  );
};

// ─── DifficultyPills ───
// Shows three pills (Easy / Medium / Difficult). The one matching `activeKey`
// is rendered with its tier color; the others are grayed-out with a tiny lock
// icon to make it visually clear they are not the current box's difficulty.

const LockIcon = ({ size = 9 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const DifficultyPills = ({ activeKey }) => {
  return (
    <div
      role="group"
      aria-label="Box difficulty"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        flexWrap: "wrap",
      }}
    >
      {DIFFICULTY_LEVELS.map((lvl) => {
        const isActive = lvl.key === activeKey;

        const baseStyle = {
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          fontSize: "10.5px",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          padding: "4px 10px",
          borderRadius: "999px",
          lineHeight: 1.2,
          userSelect: "none",
          cursor: "default",
          transition: "opacity 120ms ease",
          whiteSpace: "nowrap",
        };

        if (isActive) {
          return (
            <span
              key={lvl.key}
              title={`Difficulty: ${lvl.label}`}
              className="lab-difficulty-pill-active"
              style={{
                ...baseStyle,
                background: lvl.activeBg,
                border: `1px solid ${lvl.activeBorder}`,
                color: lvl.activeText,
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: lvl.activeDot,
                  flexShrink: 0,
                }}
              />
              {lvl.label}
            </span>
          );
        }

        // Inactive / locked
        return (
          <span
            key={lvl.key}
            title={`${lvl.label} — not this box's difficulty`}
            aria-disabled="true"
            className="lab-difficulty-pill-locked"
            style={{
              ...baseStyle,
              background: "rgba(0, 0, 0, 0.04)",
              border: "1px solid rgba(0, 0, 0, 0.15)",
              color: "rgba(0, 0, 0, 0.45)",
            }}
          >
            <LockIcon size={9} />
            {lvl.label}
          </span>
        );
      })}
    </div>
  );
};

// ─── EvidenceImage ───

const EvidenceImage = ({ src, alt, compact, onClick, onErrorFallback }) => {
  const imgRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    const img = imgRef.current;
    if (!img) return;

    if (img.complete) {
      if (img.naturalWidth > 0) {
        setStatus("loaded");
      } else {
        setStatus("error");
        onErrorFallback?.();
      }
      return;
    }

    const timeoutId = setTimeout(() => {
      setStatus((prev) => {
        if (prev === "loading") {
          onErrorFallback?.();
          return "error";
        }
        return prev;
      });
    }, IMAGE_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [src, onErrorFallback]);

  const handleLoad = () => {
    if (imgRef.current?.naturalWidth === 0) {
      setStatus("error");
      onErrorFallback?.();
      return;
    }
    setStatus("loaded");
  };

  const handleError = () => {
    setStatus("error");
    onErrorFallback?.();
  };

  if (status === "error") return null;

  return (
    <>
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#fafcfb] dark:bg-(--color-bg-card) min-h-45">
          <div className="flex items-center gap-2 text-[#888] dark:text-(--color-text-muted)">
            <div className="w-3 h-3 rounded-md border-2 border-[#d8e8df] dark:border-(--color-border) border-t-[#2e7d5e] dark:border-t-(--color-primary) animate-spin" />
            <span className="text-[11px]">Loading evidence…</span>
          </div>
        </div>
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        referrerPolicy="no-referrer"
        onLoad={handleLoad}
        onError={handleError}
        onClick={onClick}
        className={[
          "mx-auto block w-auto max-w-full object-contain transition-all duration-300 group-hover:scale-[1.015]",
          compact ? "max-h-60" : "max-h-85",
          status === "loaded" ? "opacity-100" : "opacity-0",
        ].join(" ")}
      />
    </>
  );
};

// ─── Lightbox ─────

const Lightbox = ({ src, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      className="lab-lightbox-backdrop"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close preview"
        className="lab-lightbox-close"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <img
        src={src}
        alt="Evidence preview"
        referrerPolicy="no-referrer"
        onClick={(e) => e.stopPropagation()}
        className="lab-lightbox-img"
      />
    </div>,
    document.body,
  );
};

// ─── Small presentational components ───

const QuestionsLoader = () => (
  <div className="px-9 py-6">
    <div className="rounded-md border border-[#d8e8df] bg-[#fafcfb] dark:border-(--color-border) dark:bg-(--color-bg-card) p-5">
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[11px] font-bold tracking-[0.08em] uppercase text-(--color-text-muted)">
          Loading questions
        </span>
      </div>
      <div className="space-y-3">
        <div className="h-4 bg-[#e4eee9] dark:bg-(--color-bg-dark-card-alt-2) rounded w-32" />
        <div className="h-5 bg-[#e4eee9] dark:bg-(--color-bg-dark-card-alt-2) rounded w-full" />
        <div className="h-10 bg-[#e4eee9] dark:bg-(--color-bg-dark-card-alt-2) rounded w-full" />
      </div>
    </div>
  </div>
);

const EmptyQuestions = () => (
  <div className="px-9 py-6">
    <div className="rounded-md border border-[#d8e8df] bg-[#fafcfb] dark:border-(--color-border) dark:bg-(--color-bg-card) p-5">
      <p className="text-sm text-(--color-text-muted)">
        No questions available for this lab.
      </p>
    </div>
  </div>
);

const MissionHeader = ({ title, description }) => (
  <div className="narrative-header px-9 pt-8 pb-7">
    <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-white/60">
      Mission Brief
    </div>
    <h2 className="text-[20px] font-bold text-white leading-normal">{title}</h2>
    <p className="text-sm leading-[1.72] text-white/80 mt-3">{description}</p>
  </div>
);

const ProgressBar = ({ answeredCount, totalQuestions, progressPct }) => (
  <div className="flex items-center gap-3 px-9 py-4 bg-[#f4f8f6] border-b border-[#e2e8e4] dark:bg-(--color-bg-dark-card-alt-2) dark:border-(--color-border)">
    <span className="text-[12px] font-semibold text-[#888] dark:text-(--color-text-muted) whitespace-nowrap">
      {answeredCount} / {totalQuestions} answered
    </span>
    <div className="flex-1 h-1.5 bg-[#e2ebe6] dark:bg-(--color-border-light) rounded overflow-hidden">
      <div className="progress-fill" style={{ width: `${progressPct}%` }} />
    </div>
  </div>
);

// Difficulty row — sits directly below the progress bar, pills right-aligned.
// Only renders when we actually know the box's difficulty.
const DifficultyRow = ({ difficultyKey }) => {
  if (!difficultyKey) return null;
  return (
    <div className="flex items-center justify-end px-9 py-2.5 border-b border-[#e2e8e4] dark:border-(--color-border)">
      <DifficultyPills activeKey={difficultyKey} />
    </div>
  );
};

const HintBox = ({ hint }) => (
  <div className="flex items-start gap-1.5 text-[12.5px] text-[#7a5c00] bg-[#fffbea] border border-[#f0c040] rounded-md px-3 py-2 mt-4 leading-normal dark:text-(--color-warning-text) dark:bg-(--color-bg-dark-card-alt) dark:border-(--color-warning-border)">
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    <span>Hint: {replaceScenarioIpPlaceholders(hint)}</span>
  </div>
);

// ─── EvidenceFigure ───

const EvidenceFigure = ({ q, idx, compact, onExpand, onError }) => (
  <figure className="group relative overflow-hidden rounded-md border border-[#d8e8df] bg-white dark:border-(--color-border) dark:bg-(--color-bg-dark-card-alt-2) shadow-[0_2px_8px_rgba(46,125,94,0.06)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.35)] transition-shadow hover:shadow-[0_4px_16px_rgba(46,125,94,0.1)] dark:hover:shadow-[0_4px_16px_rgba(0,0,0,0.5)]">
    {/* Header bar */}
    <div className="flex items-center justify-between px-3.5 py-2 border-b border-[#e8efeb] dark:border-(--color-border) bg-linear-to-r from-[#f4f8f6] to-[#f8fbf9] dark:from-(--color-bg-card) dark:to-(--color-bg-dark-card-alt-2)">
      <span className="text-[10.5px] font-medium text-[#888] dark:text-(--color-text-primary) truncate">
        Q{idx + 1}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onExpand();
        }}
        aria-label="View full size"
        className="flex items-center gap-1 text-[10.5px] font-semibold text-[#2e7d5e] dark:text-(--color-primary) hover:text-[#1a5a3e] dark:hover:text-(--color-primary-hover) transition-colors cursor-pointer shrink-0"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <polyline points="15 3 21 3 21 9" />
          <polyline points="9 21 3 21 3 15" />
          <line x1="21" y1="3" x2="14" y2="10" />
          <line x1="3" y1="21" x2="10" y2="14" />
        </svg>
        <span>Expand</span>
      </button>
    </div>

    {/* Clickable image area */}
    <div
      role="button"
      tabIndex={0}
      onClick={onExpand}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onExpand();
        }
      }}
      className="relative block w-full cursor-zoom-in bg-[#fafcfb] dark:bg-(--color-bg-card) overflow-hidden"
      aria-label="View evidence image full size"
    >
      <EvidenceImage
        src={q.image_ref}
        alt={`Evidence for question ${idx + 1}`}
        compact={compact}
        onErrorFallback={onError}
        onClick={(e) => {
          e.stopPropagation();
          onExpand();
        }}
      />
    </div>
  </figure>
);

// ─── QuestionCard ───

const QuestionCard = ({
  q,
  idx,
  totalQuestions,
  currentVal,
  result,
  attemptCount,
  shake,
  imageError,
  isOpen,
  submitting,
  onToggle,
  onAnswerChange,
  onSubmit,
  onExpand,
  onImageError,
  revealedAnswer,
}) => {
  const isCorrect = result === "correct";
  // A question is "locked" (out of attempts) when it isn't correct and the
  // user has burned MAX_ATTEMPTS submissions on it.
  const isLocked = !isCorrect && attemptCount >= MAX_ATTEMPTS;
  // "Incorrect" styling now means: wrong submission, but still attempts left.
  const isIncorrect = result === "incorrect" && !isLocked;
  const maxLen = q.answer_length ?? 100;
  // Hint shows after HINT_THRESHOLD wrong attempts and stays visible while
  // the card is locked, so the user can still see it next to the answer.
  const showHint = Boolean(q.hint) && attemptCount >= HINT_THRESHOLD;
  const attemptsLeft = Math.max(0, MAX_ATTEMPTS - attemptCount);

  const hasImage = Boolean(q.image_ref) && !imageError;
  const hasContext = Boolean(q.context);
  const hasTopSection = hasContext || hasImage;

  // Extract TKSA IDs from framework_alignment
  const tksaIds = q.framework_alignment?.tksa_ids ?? [];

  const cardClasses = [
    "rounded-md border transition-[border-color,box-shadow] overflow-hidden",
    isCorrect
      ? "bg-[#f0f9f5] border-[#2e7d5e] shadow-[0_0_0_1px_rgba(46,125,94,0.1)] dark:bg-(--color-primary-50) dark:border-(--color-primary)"
      : isLocked
        ? "bg-[#fff5f4] border-[#d9251d]/70 shadow-[0_0_0_1px_rgba(217,37,29,0.08)] opacity-95 dark:bg-red-500/5 dark:border-red-500/60"
        : isIncorrect
          ? "bg-[#fff5f4] border-[#d9251d] shadow-[0_0_0_1px_rgba(217,37,29,0.08)] dark:bg-red-500/5 dark:border-red-500/60"
          : "bg-[#fafcfb] border-[#d8e8df] hover:border-[#b8d8c6] dark:bg-(--color-bg-card) dark:border-(--color-border) dark:hover:border-(--color-border-medium)",
  ].join(" ");

  const inputClasses = [
    "lab-answer-input rounded-md border px-4 py-2.5 text-sm outline-none transition-colors",
    "bg-white text-[#555] border-[#e2e8e4]",
    "focus:border-[#2e7d5e] focus:ring-1 focus:ring-[#2e7d5e]/30",
    "dark:bg-(--color-bg-card) dark:text-(--color-text-secondary) dark:border-(--color-border)",
    "dark:focus:border-(--color-primary) dark:focus:ring-(--color-primary)/40",
    isCorrect &&
      "border-[#2e7d5e] bg-[#f0f9f5] text-[#1a4a3a] dark:bg-(--color-primary-50)",
    isLocked &&
      [
        "light:!border-[#d9251d]/70 light:!bg-[#fff1f0] light:!text-[#8f1d19]",
        "dark:border-red-500/60 dark:bg-red-500/10",
        "cursor-not-allowed",
      ].join(" "),
    isIncorrect &&
      [
        "light:!border-[#d9251d] light:!bg-[#ffe9e7] light:!text-[#8f1d19]",
        "light:focus:!border-[#d9251d] light:focus:!ring-[#d9251d]/35",
        "dark:border-red-500 dark:bg-red-500/10 dark:focus:border-red-500 dark:focus:ring-red-500/25",
      ].join(" "),
    shake && "shake",
  ]
    .filter(Boolean)
    .join(" ");

  // Build short status badge for the collapsed header
  let statusBadge = null;
  if (isCorrect) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 rounded-md bg-[#e6f4ec] dark:bg-(--color-primary-50) px-2 py-0.5 text-[10.5px] font-semibold text-[#2e7d5e] dark:text-(--color-primary)">
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Correct
      </span>
    );
  } else if (isLocked) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 rounded-md bg-[#fde7e5] dark:bg-red-500/10 px-2 py-0.5 text-[10.5px] font-semibold text-[#d9251d] dark:text-red-400">
        <LockIcon size={9} />
        Locked
      </span>
    );
  } else if (isIncorrect) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 rounded-md bg-[#fde7e5] dark:bg-red-500/10 px-2 py-0.5 text-[10.5px] font-semibold text-[#d9251d] dark:text-red-400">
        {attemptsLeft === 1 ? "Last try" : `${attemptsLeft} tries left`}
      </span>
    );
  }

  return (
    <div className="story-segment">
      <div className="px-9 py-6">
        <div className={cardClasses}>
          {/* Collapsible header — always visible, clickable */}
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls={`question-body-${idx}`}
            className="flex w-full items-center justify-between gap-3 px-4.5 py-3.5 text-left transition-colors hover:bg-black/2 dark:hover:bg-white/3"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span
                className={[
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[11px] font-bold",
                  isCorrect
                    ? "bg-[#2e7d5e] text-white dark:bg-(--color-primary) dark:text-(--color-text-on-primary)"
                    : isLocked || isIncorrect
                      ? "bg-[#d9251d] text-white"
                      : "bg-[#e8efeb] text-[#2e7d5e] dark:bg-(--color-bg-dark-card-alt-2) dark:text-(--color-primary)",
                ].join(" ")}
              >
                {idx + 1}
              </span>
              <span className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#2e7d5e] dark:text-(--color-primary)">
                Question {idx + 1} of {totalQuestions}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {statusBadge}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={[
                  "text-[#888] dark:text-(--color-text-muted) transition-transform duration-200",
                  isOpen ? "rotate-180" : "rotate-0",
                ].join(" ")}
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </button>

          {/* Collapsible body */}
          {isOpen && (
            <div id={`question-body-${idx}`}>
              <hr className="lab-card-divider" />

              {/* Top section: context and/or image */}
              {hasTopSection && (
                <div className="px-4.5 pt-4.5 pb-4.5">
                  {hasImage ? (
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5 lg:gap-6 items-center">
                      <div className="text-sm leading-[1.72] text-[#2d5a45] dark:text-(--color-text)">
                        {hasContext ? (
                          <div className="lab-ctx-marker">
                            {replaceScenarioIpPlaceholders(q.context)}
                          </div>
                        ) : (
                          <div className="text-[11px] font-bold uppercase text-[#888] dark:text-(--color-text-muted)">
                            Image reference for this question
                          </div>
                        )}
                      </div>
                      <EvidenceFigure
                        q={q}
                        idx={idx}
                        compact
                        onExpand={onExpand}
                        onError={onImageError}
                      />
                    </div>
                  ) : (
                    <div className="text-sm leading-[1.72] text-[#2d5a45] dark:text-(--color-text)">
                      <div className="lab-ctx-marker">
                        {replaceScenarioIpPlaceholders(q.context)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TKSA tags */}
              {tksaIds.length > 0 && (
                <div className="px-4.5 pb-3.5">
                  <TksaTagRow tksaIds={tksaIds} />
                </div>
              )}

              {(hasTopSection || tksaIds.length > 0) && (
                <hr className="lab-card-divider" />
              )}

              {/* Bottom section: full question + answer row */}
              <div className="px-4.5 py-4.5">
                <p className="text-[13px] leading-[1.6] text-[#1a1a1a] dark:text-(--color-text-primary) mb-3.5">
                  {replaceScenarioIpPlaceholders(q.question)}
                </p>

                <div className="lab-answer-row">
                  <input
                    value={
                      isLocked
                        ? revealedAnswer
                          ? `Out of attempts. The correct answer was ${replaceScenarioIpPlaceholders(revealedAnswer)}`
                          : "Locked — out of attempts"
                        : currentVal
                    }
                    onChange={onAnswerChange}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !isCorrect &&
                        !isLocked &&
                        !submitting
                      ) {
                        onSubmit();
                      }
                    }}
                    placeholder={
                      isLocked
                        ? "Locked — out of attempts"
                        : "Enter your answer"
                    }
                    maxLength={maxLen}
                    disabled={isCorrect || isLocked || submitting}
                    className={inputClasses}
                  />
                  <button
                    type="button"
                    disabled={
                      isCorrect ||
                      isLocked ||
                      submitting ||
                      currentVal.trim().length === 0
                    }
                    onClick={onSubmit}
                    className="lab-answer-btn rounded-md px-5 py-2.5 text-sm font-semibold text-white bg-[#4aab82] hover:bg-[#2e7d5e] disabled:bg-[#b2d8c8] disabled:cursor-default dark:bg-(--color-primary) dark:text-(--color-text-on-primary) dark:hover:bg-(--color-primary-hover) dark:disabled:bg-(--color-border-light)"
                  >
                    {isCorrect
                      ? "Correct"
                      : isLocked
                        ? "Locked"
                        : submitting
                          ? "Checking…"
                          : "Submit"}
                  </button>
                </div>

                {!isLocked && (
                  <div className="mt-2 flex justify-end text-xs text-[#888] dark:text-(--color-text-muted)">
                    {currentVal.length}/{maxLen}
                  </div>
                )}

                {showHint && <HintBox hint={q.hint} />}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── LabQuestionsSection (main) ───

export default function LabQuestionsSection({
  showQuestionsLoader,
  totalQuestions,
  questions,
  answers,
  submitted,
  attempts,
  setAnswers,
  setSubmitted,
  handleSubmitAnswer,
  onAttemptQuestion,
  missionTitle,
  missionDescription,
  difficulty,
  revealedAnswers = {},
}) {
  const [shakeMap, setShakeMap] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [openMap, setOpenMap] = useState({});
  // Per-question in-flight guard. Prevents double-submits while the backend
  // call for that question is pending.
  const [submittingMap, setSubmittingMap] = useState({});

  // Normalize the incoming difficulty once so the header can pick the right pill.
  const difficultyKey = normalizeDifficulty(difficulty);

  // Reset per-question UI state whenever the question set changes.
  // NOTE: we no longer reset `attempts` here — that lives in the parent and is
  // sourced from the backend so locks survive a page refresh.
  useEffect(() => {
    setShakeMap({});
    setImageErrors({});
    setSubmittingMap({});

    // Initialize open/closed: collapse correct/locked ones, open the first
    // unresolved one (or the first one if everything is resolved).
    const next = {};
    const isResolved = (i) =>
      submitted[i] === "correct" || (attempts?.[i] ?? 0) >= MAX_ATTEMPTS;
    const firstUnresolvedIdx = questions.findIndex((_q, i) => !isResolved(i));
    const idxToOpen = firstUnresolvedIdx === -1 ? 0 : firstUnresolvedIdx;
    questions.forEach((_q, i) => {
      next[i] = i === idxToOpen;
    });
    setOpenMap(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  const answeredCount = questions.reduce(
    (sum, _q, idx) => (submitted[idx] === "correct" ? sum + 1 : sum),
    0,
  );
  const progressPct = totalQuestions
    ? Math.min(100, (answeredCount / totalQuestions) * 100)
    : 0;

  const headerTitle = replaceScenarioIpPlaceholders(
    missionTitle || "Mission brief will appear once the scenario loads.",
  );
  const headerDescription = replaceScenarioIpPlaceholders(
    missionDescription ||
      "Review the mission context and answer each checkpoint based on the lab evidence.",
  );

  const triggerShake = useCallback((idx) => {
    setShakeMap((prev) => ({ ...prev, [idx]: true }));
    setTimeout(
      () => setShakeMap((prev) => ({ ...prev, [idx]: false })),
      SHAKE_DURATION_MS,
    );
  }, []);

  const toggleOpen = (idx) =>
    setOpenMap((prev) => ({ ...prev, [idx]: !prev[idx] }));

  // Collapse the just-resolved card and auto-open the next unresolved one.
  const advanceFrom = useCallback(
    (idx) =>
      setOpenMap((prev) => {
        const next = { ...prev, [idx]: false };
        const isResolved = (i) =>
          submitted[i] === "correct" || (attempts?.[i] ?? 0) >= MAX_ATTEMPTS;
        const nextUnresolved = questions.findIndex(
          (_q, i) => i > idx && !isResolved(i),
        );
        if (nextUnresolved !== -1) next[nextUnresolved] = true;
        return next;
      }),
    [questions, submitted, attempts],
  );

  const makeSubmitHandler = (idx, q) => async () => {
    if (submitted[idx] === "correct") return;
    // Guard: even if the UI somehow allowed it, never accept submissions on a
    // locked card.
    if ((attempts?.[idx] ?? 0) >= MAX_ATTEMPTS) return;
    if (submittingMap[idx]) return;

    const trimmed = (answers[idx] ?? "").trim();
    if (!trimmed) return;

    setSubmittingMap((prev) => ({ ...prev, [idx]: true }));

    let result;
    try {
      // Parent performs the backend call and updates `submitted` + `attempts`
      // from the server response, returning { isCorrect, locked, ... }.
      result = await handleSubmitAnswer(idx, q);
    } catch {
      // Parent already surfaced the error toast; just re-enable the button.
      setSubmittingMap((prev) => ({ ...prev, [idx]: false }));
      return;
    }
    setSubmittingMap((prev) => ({ ...prev, [idx]: false }));

    if (!result) return;

    onAttemptQuestion?.(idx);

    if (!result.isCorrect) {
      triggerShake(idx);
      // Backend decides the lock — auto-advance only when it reports locked.
      if (result.locked) advanceFrom(idx);
    } else {
      advanceFrom(idx);
    }
  };

  const makeChangeHandler = (idx) => (e) => {
    if (submitted[idx] === "correct") return;
    if ((attempts?.[idx] ?? 0) >= MAX_ATTEMPTS) return;
    setAnswers((prev) => ({ ...prev, [idx]: e.target.value }));
    if (submitted[idx]) {
      setSubmitted((prev) => ({ ...prev, [idx]: undefined }));
    }
  };

  const makeImageErrorHandler = (idx) => () =>
    setImageErrors((prev) => ({ ...prev, [idx]: true }));

  return (
    <div className="lab-narrative">
      <div className="narrative-flow mt-6 bg-transparent border border-(--color-border) rounded-[14px] shadow-[0_1px_3px_rgba(0,0,0,0.07)] overflow-hidden">
        <MissionHeader title={headerTitle} description={headerDescription} />

        <ProgressBar
          answeredCount={answeredCount}
          totalQuestions={totalQuestions}
          progressPct={progressPct}
        />

        <DifficultyRow difficultyKey={difficultyKey} />

        {showQuestionsLoader ? (
          <QuestionsLoader />
        ) : totalQuestions === 0 ? (
          <EmptyQuestions />
        ) : (
          questions.map((q, idx) => (
            <QuestionCard
              key={idx}
              q={q}
              idx={idx}
              totalQuestions={totalQuestions}
              currentVal={answers[idx] ?? ""}
              result={submitted[idx]}
              attemptCount={attempts?.[idx] ?? 0}
              shake={shakeMap[idx]}
              imageError={imageErrors[idx]}
              isOpen={openMap[idx] ?? false}
              submitting={submittingMap[idx] ?? false}
              onToggle={() => toggleOpen(idx)}
              onAnswerChange={makeChangeHandler(idx)}
              onSubmit={makeSubmitHandler(idx, q)}
              onExpand={() => setLightboxImage(q.image_ref)}
              onImageError={makeImageErrorHandler(idx)}
              revealedAnswer={revealedAnswers[idx]}
            />
          ))
        )}
      </div>

      {lightboxImage && (
        <Lightbox src={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}
    </div>
  );
}
